package com.crowpay.views.components.project.work.payApps

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.sdk.creationError
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.utils.tables.ColumnWeights
import com.crowpay.utils.tables.trackWeights
import com.crowpay.utils.validation.Validator
import com.crowpay.utils.validation.validate
import com.crowpay.views.components.infoBubble
import com.crowpay.views.components.leftExpandButton
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.scopeSetIcon
import com.crowpay.views.components.tableEntry
import com.crowpay.views.components.tableEntryString
import com.crowpay.views.dialogs.GenericConfirmationDialog

import com.crowpay.views.screens.common.LandingScreen
import com.crowpay.views.screens.contractor.ContractorPage
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.ConnectionException
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.printStackTrace2
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.roundToInt
import kotlin.math.roundToLong

suspend fun CreatePayApp(project: UUID): PayAppForm {
    val session = notNullSession()
    val numPayApps = session.payApplications.query(
        Query { it.project eq project }
    )().size

    val new = session.payApplications.insert(
        PayApplication(
            project = project,
            number = numPayApps + 1
        )
    )() ?: creationError<PayApplication>()

    return PayAppForm(new._id)
}

//private object TableWeights {
//    val columnSpacing = 0.5.rem
//
//    const val workItem = 2f
//    const val approvedAmount = 1.5f
//    const val pastPayments = 1f
//    const val thisDraw = 1f
//    const val totalCompleted = 1f
//    const val percentCompleted = 1f
//    const val remaining = 1f
//    const val retainage = 1f
//}

private fun LineItemState.drawable(): Boolean = this in setOf(LineItemState.InProgress, LineItemState.NotApproved)

@Routable("draws/{payAppID}/edit")
class PayAppForm(val payAppID: UUID) : ContractorPage, Validator() {
    val payApp = Draft {
        notNullSession().payApplications[payAppID]() ?: notFoundError<PayApplication>()
    }
    val project = shared {
        notNullSession().projects[payApp().project]() ?: notFoundError<Project>()
    }

    override val title: Readable<String> = shared { "Draw ${payApp().number}" }

    private val _lineItems = shared {
        notNullSession().lineItems.query(
            Query(
                limit = Int.MAX_VALUE,
                condition = condition {
                    it.project eq project()._id
                }
            )
        )
    }
    private val _itemChanges = shared {
        notNullSession().itemChanges.query(
            Query(
                limit = Int.MAX_VALUE,
                condition = condition {
                    it.project eq project()._id
                }
            )
        )
    }
    val lineItems = shared {
        val items = _lineItems()()
        val changes = _itemChanges()()
        items.map { item ->
            AdjustedLineItem(item, changes.filter { it.itemId == item._id })
        }
    }
    private val _scopes = shared {
        notNullSession().scopeViews.query(
            Query(
                condition { it.project eq project()._id },
                limit = Int.MAX_VALUE,
            )
        )
    }
    val scopes = shared {
        _scopes()()
    }

    val formCache = HashMap<UUID, PayAppItemForm>()
    private val _appPayItems = shared {
        notNullSession().payAppItems.query(
            Query(
                limit = Int.MAX_VALUE,
                condition = condition { (it.project eq project()._id) }
            )
        )
    }
    val allPayItems = shared {
        _appPayItems()()
    }

    val existingItemForms = shared(useLastWhileLoading = true) {
        val allItems = allPayItems().filter { it.status != PayAppStatus.Voided && it.status != PayAppStatus.Denied }

        val existing = allItems.filter { it.payApp == payAppID }

        val lines = lineItems()
        val project = project()

        existing
            .map { payAppItem ->
                val line =
                    lines.find { it._id == payAppItem.lineItem } ?: notFoundError<LineItem>()

                formCache.getOrPut(line._id) {
                    PayAppItemForm(
                        payAppItem,
                        line,
                        project.safeRetention,
                        previousPayments = allItems
                            .filter { it !in existing && it.lineItem == line._id && it.approved != null }
                            .sumOf { it.amount },
                        pendingPayments = allItems
                            .filter { it !in existing && it.lineItem == line._id && it.pending }
                            .sumOf { it.amount },
                        this@PayAppForm
                    )
                }
            }
            .sortedBy { it.lineItem.wraps.created }
    }

    val missingItemForms = shared(useLastWhileLoading = true) {
        val allItems = allPayItems().filter { it.status != PayAppStatus.Voided && it.status != PayAppStatus.Denied }

        val lines = lineItems()
        val project = project()

        val existingLineIDs = allItems.filter { it.payApp == payAppID }.map { it.lineItem }

        lines
            .filter { it._id !in existingLineIDs }
            .map { line ->
                formCache.getOrPut(line._id) {
                    PayAppItemForm(
                        if (line.state.drawable()) PayAppItem(
                            payApp = payAppID,
                            lineItem = line._id,
                            amount = 0L,
                            project = project._id,
                        )
                        else null,
                        line,
                        project.safeRetention,
                        previousPayments = allItems
                            .filter { it.lineItem == line._id && it.approved != null }
                            .sumOf { it.amount },
                        pendingPayments = allItems
                            .filter { it.lineItem == line._id && it.pending }
                            .sumOf { it.amount },
                        this@PayAppForm
                    )
                }
            }
            .sortedBy { it.lineItem.wraps.created }
    }

    val allItemForms = shared(useLastWhileLoading = true) {
        (existingItemForms() + missingItemForms()).sortedBy { it.lineItem.wraps.created }
    }

    val thisDraw = shared { allItemForms().sumOf { it.draw() ?: 0.0 }.toLong() }
    val totalWorkCompleted = shared { allItemForms().sumOf { it.workCompleted } + thisDraw() }

    private suspend fun save() {
        val session = notNullSession()
        payApp.pushChanges(session.payApplications)
        coroutineScope {
            allItemForms().forEach {
                launch { it.save() }
            }
        }
    }

    private suspend fun ViewWriter.deleteAndExit() {
        screenNavigator.goBack().also {
            delay(500)
            notNullSession().payApplications[payApp()._id].delete()
        }
    }

    fun ViewWriter.exit() {
        if (!screenNavigator.goBack()) screenNavigator.reset(LandingScreen())
    }

    fun ViewWriter.actionBar() {
        fun ViewWriter.saveButton() = secondaryButton - button {
            specCenteredText("Save")
            action = Action("Save") { save() }
        }

        fun ViewWriter.saveAndCloseButton() = secondaryButton - button {
            specCenteredText("Save & Close")
            action = Action("Save and Exit") { save(); exit() }
        }

        fun ViewWriter.discardButton() = deleteButton - button {
            specCenteredText("Discard")
            onClick("Discard Draft") {
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = "Discard Work",
                        message = "Are you sure you want to discard? Any unsaved changes will be lost.",
                        confirmationText = "Discard",
                        onSubmit = { if (it) exit() }
                    )
                )
            }
        }

        fun ViewWriter.deleteButton() = onlyWhen { payApp().submitted == null } - deleteButton - button {
            centered - icon(Icon.trash.copy(width = 1.5.rem, height = 1.5.rem), "Delete Draw Draft")
            onClick {
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = "Delete Draft?",
                        message = "You are about to delete this draw draft. Deleted drafts cannot be recovered. Delete this draft?",
                        confirmationText = "Delete Draft",
                        confirmationTheme = DangerButtonSemantic,
                        onSubmit = {
                            if (it) {
                                deleteAndExit()
                            }
                        }
                    )
                )
            }
        }

        fun ViewWriter.submitButton() = primaryButton - button {
            ::enabled {
                thisDraw() > 0 && allItemForms().all { it.draw.valid() }
            }
            specCenteredText("Submit")
            action = Action("Submit") {
                save()
                val session = notNullSession()
                session.nonCached.payApplication.submitPayApplication(payAppID)
                session.payApplications.totallyInvalidate()
                session.payAppItems.totallyInvalidate()
                exit()
            }
        }

        if (viewIsNarrow) section - row {
            spacing = AppDimensions.buttonRowSpacing
            deleteButton()
            expanding - space()
            saveButton()
            submitButton()
        }
        else section - row {
            saveButton()
            saveAndCloseButton()
            discardButton()
            deleteButton()
            expanding - space()
            submitButton()
        }
    }

    private fun ViewWriter.drawSummary() = stack {
        spacing = 5.dp

        val rowSpacing = 1.rem

        fun ViewWriter.projectValueStatement(): ViewModifiable {
            fun RowOrCol.tableRow(
                key: String,
                value: ReactiveContext.() -> String
            ) =
                col {
                    spacing = 0.px
                    row {
                        expanding - body(key)
                        body {
                            align = Align.Center
                            ::content.invoke(value)
                        }
                    }
                    greySeparator()
                }

            return sizeConstraints(width = 25.rem) - col {
                spacing = rowSpacing
                tableRow("Original Project Price") { project().originalPrice.renderDollars() }
                tableRow("Accepted Change Amount") {
                    project().let { it.activePrice - it.originalPrice }.renderDollars()
                }
                tableRow("Active Price") { project().activePrice.renderDollars() }
                tableRow("Retention") { "${project().safeRetention.times(100).roundToInt()}%" }
            }
        }

        fun ViewWriter.totalDrawsToDate() {
            val keyWeight = 2.5f
            val valueWeight = 1.25f
            val valueSpacing = 4.dp

            fun RowOrCol.tableRow(
                key: String,
                value: ReactiveContext.() -> String
            ) {
                row {
                    weight(keyWeight) - centered - body(key)
                    weight(valueWeight) - lightSection - body {
                        spacing = valueSpacing
                        align = Align.Center
                        ::content.invoke(value)
                    }
                }
            }

            sizeConstraints(width = 25.rem) - col {
                spacing = rowSpacing - valueSpacing
                row {
                    weight(keyWeight) - centered - body("Work Completed to Date")
                    weight(valueWeight) - lightSection - stack {
                        spacing = valueSpacing
                        centered - row {
                            spacing = 5.dp
                            body { ::content { totalWorkCompleted().renderDollars() } }
                            centered - smallBody {
                                ::content {
                                    "(${totalWorkCompleted().times(100) / project().activePrice}%)"
                                }
                            }
                        }
                    }
                }
                tableRow("Less Retention To Date") {
                    (totalWorkCompleted() * project().safeRetention).roundToLong().renderDollars()
                }
                tableRow("Less Previous Payments") { project().contractorPayments.renderDollars() }
                separator()
                row {
                    weight(keyWeight) - centered - body("= Amount Due Request")
                    weight(valueWeight) - lightSection - redText - body {
                        spacing = valueSpacing
                        align = Align.Center
                        ::content {
                            (thisDraw() * (1 - project().safeRetention)).roundToLong().renderDollars()
                        }
                    }
                }
            }
        }

        rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
            spacing = AppDimensions.majorColumnSpacing * 1.5
            col {
                rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
                    space(0.1)
                    bodyBold("Project Value Breakdown")
                }
                lightSection - projectValueStatement()
            }
            col {
                bodyBold("Total Draws to Date")
                totalDrawsToDate()
            }
        }
    }

    private fun ViewWriter.table() = col {
        col {
            trackWeights {
                columnSpacing = 0.5.rem

                subTitle {
                    ::content { "Draw ${payApp().number}: ${payApp().created.format(Formats.mmddyyyy)}" }
                }

                row {
                    spacing = columnSpacing

                    fun ViewWriter.columnLabel(
                        info: String,
                        content: RowOrCol.() -> ViewModifiable
                    ) =
                        stack {
                            centered - row {
                                spacing = 5.dp
                                centered - infoBubble(info)
                                centered - content()
                                space(0.0)
                            }
                        }

                    fun ViewWriter.columnLabel(name: String, info: String) = columnLabel(info) {
                        bodyBold {
                            wraps = true
                            content = name
                        }
                    }

                    setColWeight(1.5f) - atBottomStart - bodyBold("Work Item")
                    setColWeight(1.5f) - atBottomCenter - columnLabel("Approved", "Approved work including changes")
                    setColWeight(1f) - atBottomCenter - columnLabel("Processed", "Draw items that have been paid and work items that have been signed off as done.")
                    setColWeight(1f) - atBottomCenter - columnLabel("Pending", "Work items that are in Request Sign-Off status or draw items that are open.")
                    setColWeight(1f) - atBottomCenter - columnLabel("Draw", "Value of work being submitted for this draw.")
                    setColWeight(1.5f) - atBottomCenter - columnLabel("All work values from processed, pending, and this draw") {
                        bodyBold("Work To-Date")
                        smallBody("(%)")
                    }
                    setColWeight(1f) - atBottomCenter - columnLabel("Remaining", "Remaining work value on that work item.")
                    setColWeight(1f) - atBottomCenter - columnLabel("The portion of retention total allocated to this item based on Work To-Date.") {
                        atBottomStart - bodyBold {
                            wraps = true
                            ::content { "Retention Portion (${project().safeRetention.times(100).roundToLong()}%)" }
                        }
                    }
                }
                greySeparator()

                col {
                    val groupedForms = shared(useLastWhileLoading = true) {
                        val allScopes = scopes().associateBy { it._id }
                        val grouped = allItemForms().groupBy { it.lineItem.scopeView }

                        grouped
                            .mapKeys { (scopeId, _) -> allScopes[scopeId] }
                            .toList()
                            .sortedBy { it.first?._id }
                    }

                    forEach(groupedForms) { (scope, forms) ->
                        col group@{
                            if (scope != null) col {
                                space(0.25)
                                compact - lightSection - row {
                                    if (scope.scopeSet) centered - scopeSetIcon(1.rem)
                                    body(scope.scopeTitle)
                                }
                            }
                            for (form in forms) {
                                form.tableRow(this@group, this@trackWeights)
                                greySeparator()
                            }
                        }
                    }
                }

                row {
                    spacing = columnSpacing

                    fun ViewWriter.total(calculation: ReactiveContext.() -> Long) = bodyBold {
                        align = Align.Center
                        wraps = true
                        ::content { calculation().renderDollars() }
                    }
                    getColWeight - bodyBold("Totals")
                    getColWeight - total { lineItems().sumOf { it.price } }
                    getColWeight - total { allItemForms().sumOf { it.processed } }
                    getColWeight - total { allItemForms().sumOf { it.pending } }
                    getColWeight - total { thisDraw() }
                    getColWeight - stack {
                        centered - row {
                            spacing = 5.dp
                            bodyBold {
                                ::content { totalWorkCompleted().renderDollars() }
                            }
                            smallBody {
                                align = Align.Center
                                ::content { "(${totalWorkCompleted().times(100) / project().activePrice}%)" }
                            }
                        }
                    }
                    getColWeight - total { project().activePrice - totalWorkCompleted() }
                    getColWeight - total { (totalWorkCompleted() * project().safeRetention).roundToLong() }
                }
            }
        }

        space(0.25)

        col {
            subTitle("This Draw")
            fun ViewWriter.money(calculation: ReactiveContext.() -> Long) = body {
                align = Align.Center
                ::content { calculation().renderDollars() }
            }

            row {
                lightSection - stack {
                    centered - col {
                        spacing = AppDimensions.buttonSpacing
                        money { thisDraw() }
                        centered - body("Work Value")
                    }
                }
                centered - bodyBold("-")
                lightSection - stack {
                    centered - col {
                        spacing = AppDimensions.buttonSpacing
                        money { (thisDraw() * project().safeRetention).roundToLong() }
                        centered - body("Retention")
                    }
                }
                centered - bodyBold("=")
                lightSection - stack {
                    centered - col {
                        spacing = AppDimensions.buttonSpacing
                        redText - money { (thisDraw() * (1 - project().safeRetention)).roundToLong() }
                        centered - body("Payment")
                    }
                }
            }
        }
    }

    private fun ViewWriter.renderWide() = scrolls - stack {
        gravity(Align.Start, Align.Start) - sizeConstraints(width = AppDimensions.pageWidth) - col {
            spacing = AppDimensions.majorColumnSpacing

            title { ::content { "${project().name} Draw ${payApp().number}" } }

            actionBar()

            subTitle("Draw Summary")
            centered - drawSummary()

            actionBar()

            table()

            space(10.0)
        }
    }

    private enum class Section(override val displayName: String) : HasDisplayName {
        Summary("Summary"), Draws("Draws")
    }

    private fun ViewWriter.renderNarrow() = col {
        val selectedSection = Property(Section.Summary)

        expanding - scrolls - col {
            title { ::content { "${project().name} Draw ${payApp().number}" } }

            InnerTabBar.onNext - row {
                for (section in Section.entries) {
                    expanding - buttonTheme - radioToggleButton {
                        centered - body(section.displayName)
                        checked bind selectedSection.equalTo(section)
                    }
                }
            }

            stack {
                reactive {
                    clearChildren()
                    when (selectedSection()) {
                        Section.Summary -> drawSummary()
                        Section.Draws -> drawCards()
                    }
                }
            }
        }

        actionBar()
    }

    private fun ViewWriter.drawCards() = col {
        atEnd - tertiaryButton - button {
            specCenteredText("+ Draws")
            onClick("Add Draws") {
                dialogScreenNavigator.navigate(
                    SelectItemsDialog()
                )
            }
        }

        col {
            spacing = AppDimensions.majorColumnSpacing

            val groupedForms = shared(useLastWhileLoading = true) {
                val allScopes = scopes().associateBy { it._id }

                existingItemForms()
                    .groupBy { it.lineItem.scopeView }
                    .mapKeys { (scopeId, _) -> allScopes[scopeId] }
                    .toList()
                    .sortedBy { it.first?.scopeTitle }
            }

            forEach(groupedForms) { (scope, items) ->
                ListSemantic.onNext - col {
                    if (scope != null) compact - row {
                        if (scope.scopeSet) centered - scopeSetIcon(1.2.rem)
                        centered - bodyBold(scope.scopeTitle)
                    }
                    for (item in items) {
                        item.card(this)
                    }
                }
            }
        }
    }

    override fun ViewWriter.renderMainContent() {
        if (viewIsNarrow) renderNarrow()
        else renderWide()
    }

    private inner class SelectItemsDialog : Page {
        val previouslySelected = shared { existingItemForms().map { it.lineItem._id }.toSet() }
        val selectedIds = LazyProperty { previouslySelected() }

        val groupedForms = shared(useLastWhileLoading = true) {
            val allScopes = scopes().associateBy { it._id }
            val grouped = allItemForms().groupBy { it.lineItem.scopeView }

            grouped
                .mapKeys { (scopeId, _) -> allScopes[scopeId] }
                .toList()
                .sortedBy { it.first?._id }
        }

        override fun ViewWriter.render2(): ViewModifiable = dismissBackground {
            spacing = AppDimensions.fullIndent
            gravity(Align.Stretch, Align.Center) - dialog - stack {
                spacing = AppDimensions.fullIndent
                removeOutline - col {
                    spacing = AppDimensions.majorColumnSpacing

                    col {
                        title("Add Draws")

                        body("Select which items you want to add draws for.")
                    }

                    trackWeights {
                        col {
                            spacing = AppDimensions.labelSpacing
                            row {


                                spacing = AppDimensions.buttonRowSpacing
                                setColWeight(0.4f) - space()
                                setColWeight(3f) - smallBody("Work Item")
                                setColWeight(1.1f) - smallBody {
                                    align = Align.Center
                                    content = "Remaining"
                                }
                            }
                            greySeparator()
                            sizeConstraints(minHeight = 20.rem, maxHeight = 35.rem) - scrolls - col {
                                spacing = 1.rem

                                ListSemantic.onNext - col {
                                    forEach(groupedForms) { (scope, items) ->
                                        if (scope != null) LightSection.onNext - compact - row {
                                            if (scope.scopeSet) centered - scopeSetIcon(1.rem)
                                            centered - body(scope.scopeTitle)
                                        }
                                        for (option in items) {
                                            val selected = selectedIds.contains(option.lineItem._id)
                                            RemoveToggleSemantic.onNext - toggleButton {
                                                enabled = option.drawable
                                                spacing = 4.dp
                                                compact - row {
                                                    spacing = AppDimensions.buttonRowSpacing
                                                    getColWeight - centered - checkbox {
                                                        enabled = option.drawable
                                                        checked bind selected
                                                    }
                                                    getColWeight - centered - body {
                                                        content = option.lineItem.name
                                                        wraps = false
                                                        ellipsis = true
                                                    }
                                                    getColWeight - centered - (option.ineligibleWarning(this) ?: body {
                                                        content = option.lineItem.wraps.remainingFunds.renderDollars()
                                                        align = Align.Center
                                                    })
                                                }
                                                checked bind selected
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    atEnd - row {
                        secondaryButton - button {
                            specCenteredText("Go Back")
                            onClick("Dismiss") {
                                dialogScreenNavigator.dismiss()
                            }
                        }
                        primaryButton - button {
                            ::enabled { selectedIds() != previouslySelected() }
                            specCenteredText {
                                val sel = selectedIds()
                                val prev = previouslySelected()
                                val added = sel.count { it !in prev }
                                val removed = prev.count { it !in sel }
                                when {
                                    added > 0 && removed > 0 -> "Add ($added), Remove ($removed)"
                                    added > 0 -> "Add ($added) Draws"
                                    removed > 0 -> "Remove ($removed) Draws"
                                    else -> "Select Items"
                                }
                            }
                            onClick("Add Selected") {
                                val selected = selectedIds()
                                val previouslySelected = previouslySelected()

                                val added = selected - previouslySelected
                                if (added.isNotEmpty()) {
                                    coroutineScope {
                                        missingItemForms()
                                            .filter { it.lineItem._id in added }
                                            .forEach {
                                                launch { it.save(saveIfNull = true) }
                                            }
                                    }
                                }

                                val removed = previouslySelected - selected
                                if (removed.isNotEmpty()) {
                                    coroutineScope {
                                        existingItemForms()
                                            .filter { it.lineItem._id in removed }
                                            .forEach {
                                                launch { it.delete(formCache) }
                                            }
                                    }
                                }

                                dialogScreenNavigator.dismiss()
                            }
                        }
                    }
                }
            }
        }
    }

    class PayAppItemForm(
        val item: PayAppItem?,  // Null indicates you cannot create a pay item for this work item (either the line item state disallows it or there is an existing pay app item for it)
        val lineItem: AdjustedLineItem,
        val retention: Float,
        val previousPayments: Long,
        pendingPayments: Long,
        reportTo: Validator
    ) : Validator(reportTo) {
        val pending = if (lineItem.state == LineItemState.RequestingReview) lineItem.price - previousPayments else pendingPayments
        val processed = if (lineItem.state.isComplete()) lineItem.price else previousPayments

        val workCompleted = processed + pending

        val maxDrawable = lineItem.price - workCompleted

        val draw = Property(item?.amount?.takeUnless { it <= 0L }?.toDouble())
            .validate {
                if (it == null) true
                else it > 0.0 && it <= maxDrawable
            }

        val drawable = (item != null) and lineItem.state.drawable()

        val totalCompleted = shared { (workCompleted + (draw() ?: 0.0).toLong()).coerceAtMost(lineItem.price) }

        suspend fun save(saveIfNull: Boolean = false) {
            val payItem = item ?: return
            val amount = draw()?.toLong() ?: if (saveIfNull) 0L else return

            notNullSession().payAppItems.run {
                val model = get(payItem._id)

                if (model.awaitOnce() != null) model.modify2 { it.amount assign amount }
                else insert(payItem.copy(amount = amount))
            }
        }

        suspend fun delete(cache: MutableMap<UUID, PayAppItemForm>) {
            if (item == null) return
            try {
                notNullSession().payAppItems[item._id].delete()
            } catch (e: ConnectionException) {
                e.printStackTrace2()
                notNullSession().payAppItems.localSignalUpdate(
                    matching = { it.lineItem == lineItem._id },
                    modify = { null }
                )
            }
            cache.remove(lineItem._id)
        }

        fun ineligibleWarning(writer: ViewWriter): ViewModifiable? = with(writer) {
            val lineItemStatus: String? =
                when (lineItem.state) {   // These status names are only used here, so not worth its own field on the enum
                    LineItemState.InProgress -> null
                    LineItemState.NotApproved -> null

                    LineItemState.Complete,
                    LineItemState.Resolved -> "Paid"

                    LineItemState.Cancelled -> "N/A"

                    else -> "Ineligible"
                }

            when {
                lineItemStatus == "Ineligible" -> {
                    stack {
                        centered - row {
                            spacing = 0.5.rem
                            centered - infoBubble("Work item has not been started, or is not in the Working status")
                            centered - smallBody {
                                content = lineItemStatus
                            }
                        }
                    }
                }

                lineItemStatus != null -> {
                    smallBody {
                        align = Align.Center
                        content = lineItemStatus
                    }
                }

                lineItem.price - previousPayments <= 0 -> {
                    centered - smallBody {
                        align = Align.Center
                        content = "Needs Sign Off"
                    }
                }

                else -> null
            }
        }

        private fun ViewWriter.workCompleted() = row {
            spacing = 3.dp
            centered - body {
                ::content { totalCompleted().renderDollars() }
            }
            centered - smallBody {
                ::content {
                    lineItem.price
                        .takeUnless { it <= 0 }
                        ?.let { "(${totalCompleted().times(100) / it}%)" }
                        ?: ""
                }
            }
        }

        private fun ViewWriter.drawInput() =
            fieldTheme - validate(draw) { draw() != null } - row {
                spacing = if (viewIsNarrow) 5.dp else 0.2.rem
                centered - smallBody("$")
                centered - numberInput {
                    if (viewIsNarrow) hint = "Max ${maxDrawable.renderDollars()}"
                    align = Align.Start
                    keyboardHints = KeyboardHints.integer
                    spacing = if (viewIsNarrow) 5.dp else 0.2.rem
                    content bind draw
                }
            }

        fun card(writer: ViewWriter) = with(writer) {
            val expanded = Property(false)

            lightSection - col {
                spacing = theme.spacing / 2
                row {
                    spacing = 3.dp
                    centered - expanding - leftExpandButton(expanded, highlightOnExpand = false) {
                        expanding - centered - bodyBold(lineItem.name)
                    }
                    centered - sizeConstraints(width = 8.rem) - centered - drawInput()
                }

                onlyWhen { expanded() } - padded - col {
                    tableEntryString("Approved Amount", lineItem.price.renderDollars())
                    tableEntryString("Processed", processed.renderDollars())
                    tableEntryString("Pending", pending.renderDollars())
                    tableEntry("Work Completed") { workCompleted() }
                    tableEntryString("Remaining") { (lineItem.price - totalCompleted()).renderDollars() }
                    tableEntryString("Retention") { (totalCompleted() * retention).roundToLong().renderDollars() }
                }
            }
        }

        fun tableRow(writer: ViewWriter, columns: ColumnWeights) = with(writer) {
            with(columns) {
                sizeConstraints(height = 1.5.rem) - row {
                    spacing = 0.px
                    getColWeight - centered - row {
                        spacing = AppDimensions.expandButtonSpace
                        body {
                            if (lineItem.state == LineItemState.Cancelled) themeChoice += GreyedOutSemantic
                            content = lineItem.name
                        }
                        centered - smallBody { ::content { lineItem.changeType?.postTense ?: "" } }
                    }

                    weight(
                        weights.drop(1).sum()  /*Correction for spacing in columns*/
                    ) - row {
                        spacing = columnSpacing
                        if (!drawable) themeChoice += GreyedOutSemantic
                        getColWeight - centered - body {
                            align = Align.Center
                            content = lineItem.price.renderDollars()
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            content = processed.renderDollars()
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            content = pending.renderDollars()
                        }

                        getColWeight - centered - (ineligibleWarning(this) ?: drawInput())

                        getColWeight - stack {
                            centered - workCompleted()
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            ::content {
                                (lineItem.price - totalCompleted()).renderDollars()
                            }
                        }
                        getColWeight - centered - body {
                            align = Align.Center
                            ::content { (totalCompleted() * retention).roundToLong().renderDollars() }
                        }
                    }
                }
            }
        }
    }
}